<template>
  <div class="activity-stage digital-citizenship">
    <section v-if="!showCongratsScreen" transition="fade">
      <h3 v-if="!showCongratsScreen">{{ currentRound.questionText }}</h3>
      <h4 transition="fade">{{ currentRound.instructions }}</h4>
    </section>
    <section class="sub-template-container" v-if="!showCongratsScreen" transition="fade">
      <drag-and-drop
        v-if="currentRound.type === 'dragAndDrop'"
        transition="fade"
        :key="currentRound.roundIndex"
        :round-index="currentRound.roundIndex"
        :steps="currentRound.steps"
        :image-path="currentRound.image"
        :answer-position="currentRound.answerPosition.toLowerCase()"
        :on-drop-label="handleStepEnd"
      ></drag-and-drop>
      <drop-down-list
        v-if="currentRound.type === 'dropDownList'"
        transition="fade"
        :key="currentRound.roundIndex"
        :round-index="currentRound.roundIndex"
        :steps="currentRound.steps"
        :on-select-item="handleStepEnd"
      ></drop-down-list>
      <single-select
        transition="fade"
        v-if="currentRound.type === 'singleSelect'"
        :key="currentRound.roundIndex"
        :round-index="currentRound.roundIndex"
        :steps="currentRound.steps"
        :on-select="handleStepEnd"
      ></single-select>
      <multi-select
        transition="fade"
        v-if="currentRound.type === 'multiSelect'"
        :key="currentRound.roundIndex"
        :round-index="currentRound.roundIndex"
        :steps="currentRound.steps"
        :on-check="handleStepEnd"
      ></multi-select>
      <video-player
        transition="fade"
        v-if="currentRound.type === 'videoLesson'"
        :key="currentRound.roundIndex"
        :round-index="currentRound.roundIndex"
        :url="currentRound.video"
        :on-video-end="handleStepEnd"
      ></video-player>
    </section>
    <figure class="congrats" transition="fade" v-if="showCongratsScreen">
      <img :src="assessmentCompletePath" />
    </figure>
    <footer
      class="progress-bar"
      v-if="!showCongratsScreen && currentRound.type !== 'videoLesson'"
      transition="fade"
    >
      <progress-bar-container
        :completed-units="lastRoundWasFinished ? activityData.rounds.length : progress.roundIndex"
        :total-units="activityData.rounds.length"
      ></progress-bar-container>
    </footer>
    <static-intro-button :enabled="canAdvance" :on-click="handleRoundEnd"></static-intro-button>
  </div>
</template>

<script>
import { mapState } from 'vuex';
import { TweenMax } from 'gsap';
import { isEmpty } from 'lodash';

import ActivityManager from 'mixins/ActivityManager';
import TypingActivityHelper from 'mixins/TypingActivityHelper';
import ErrorLogger from 'mixins/ErrorLogger';

import ProgressBarContainer from 'components/activity/progress-bars/ProgressBarContainer.vue';
import StaticIntroButton from 'components/buttons/StaticIntroButton.vue';

import assessmentCompletePath from 'assets/images/assessment-complete.png';

import DragAndDrop from 'components/activity/ADigitalCitizenship/DragAndDrop.vue';
import DropDownList from 'components/activity/ADigitalCitizenship/DropDownList.vue';
import SingleSelect from 'components/activity/ADigitalCitizenship/SingleSelect.vue';
import MultiSelect from 'components/activity/ADigitalCitizenship/MultiSelect.vue';
import VideoPlayer from 'components/activity/ADigitalCitizenship/VideoPlayer.vue';
import KeyboardingAPI from '../api';

export default {
  name: 'DigitalCitizenship',
  components: {
    DragAndDrop,
    DropDownList,
    SingleSelect,
    MultiSelect,
    VideoPlayer,
    ProgressBarContainer,
    StaticIntroButton
  },

  mixins: [ActivityManager, ErrorLogger, TypingActivityHelper],

  data() {
    return {
      sfx: [],
      activityName: 'ADigitalCitizenship',
      canAdvance: false,
      showCongratsScreen: false,
      assessmentCompletePath,
      answers: {},
      retryAttempt: 0,
      maxRetries: 3,
      lastRoundWasFinished: false
    };
  },

  beforeDestroy() {
    TweenMax.killAll();
  },

  computed: {
    ...mapState('student', {
      studentId: state => state.id,
      grade: state => state.grade || ''
    }),
    ...mapState('activity', {
      activityId: state => state.data.activityId,
      scheduledActivityId: state => state.data.scheduledActivityId || null,
      activityType: state => state.data.type || null,
      activityProgress: state =>
        state.data.progress ? parseInt(state.data.progress.currentRoundId, 10) : null
    })
  },

  methods: {
    startActivity() {
      this.errorLoggerStart();
      const { activityProgress, rounds } = this;
      if (activityProgress !== null) {
        if (activityProgress < rounds.length - 1) {
          this.jumpToRound(activityProgress);
        } else if (activityProgress >= rounds.length - 1) {
          this.completeActivity();
        }
      }

      if (!this.showCongratsScreen) {
        this.setupNewRound();
        this.playRoundAudioInstructions();
      }
    },

    async loadActivity() {
      const { rounds } = this.activityData;
      this.createRounds({
        rounds,
        instructions: {
          written: '',
          voice: [''],
          delay: 4
        }
      });

      this.sfx = await this.loadAssets();
    },

    async loadAssets() {
      const resolutions = await Promise.all([this.loadImages(), this.loadRoundAudioInstructions()]);

      return resolutions[1];
    },

    async loadImages() {
      this.rounds.forEach(round => {
        if (round.image) {
          this.queueImage(round.image);
        }
        if (round.steps) {
          round.steps.forEach(step => {
            if (step.image) {
              this.queueImage(step.image);
            }
          });
        }
      });

      return this.downloadImages();
    },

    getKeyboardConfig() {
      return {
        showKeyboardAtStart: false
      };
    },

    async loadRoundAudioInstructions() {
      const soundPaths = [];

      this.rounds.forEach(({ audio }) => {
        soundPaths.push(audio);
        this.queueAudio(audio);
      });

      const cache = await this.downloadAudio();

      return soundPaths.map(audioPath => cache.get(audioPath));
    },

    playRoundAudioInstructions() {
      const snd = this.sfx[this.progress.roundIndex] || null;
      if (snd) snd.play();
    },

    stopRoundAudioInstructions() {
      const snd = this.sfx[this.progress.roundIndex] || null;
      if (snd) snd.stop();
    },

    handleStepEnd(activityType, roundIndex, answerData) {
      if (activityType === 'video') {
        this.sndBell();
      } else {
        this.sndType();
      }
      if (activityType === 'dragAndDrop') {
        if (
          answerData &&
          !isEmpty(answerData) &&
          answerData.length === this.currentRound.steps.length
        ) {
          this.answers[roundIndex] = {
            activityType,
            roundIndex,
            answerData
          };

          this.canAdvance = true;
        } else {
          this.canAdvance = false;
        }
      } else if (answerData && !isEmpty(answerData)) {
        this.answers[roundIndex] = {
          activityType,
          roundIndex,
          answerData
        };
        this.canAdvance = true;
      } else {
        this.canAdvance = false;
      }
    },

    handleRoundEnd() {
      if (this.showCongratsScreen) {
        this.completeActivity();
      } else {
        TweenMax.killAll();
        this.stopRoundAudioInstructions();
        this.canAdvance = false;
        if (this.scheduledActivityId) {
          this.postProgressData();
        } else {
          this.advanceRound();
        }
      }
    },

    advanceRound() {
      if (this.progress.roundIndex < this.rounds.length - 1) {
        this.errorLoggerStart();
        this.setupNewRound();
        this.playRoundAudioInstructions();
        this.retryAttempt = 0;
      } else {
        this.lastRoundWasFinished = true;
        TweenMax.delayedCall(1, () => {
          this.showCongratsScreen = true;
          this.canAdvance = true;
        });
      }
    },

    async postProgressData() {
      const isNotVideo = this.answers[this.currentRound.roundIndex].activityType !== 'video';

      const containsErrors = isNotVideo
        ? // eslint-disable-next-line
          this.answers[this.currentRound.roundIndex].answerData.some(
            answer => !answer.isCorrectAnswer
          )
        : false;

      const errorData = containsErrors
        ? [
            {
              roundIndex: this.currentRound.roundIndex,
              answer: [
                // eslint-disable-next-line
                ...this.answers[this.currentRound.roundIndex].answerData.map(
                  ({ answer, text, isCorrectAnswer: correct }) => ({
                    answer: answer || text,
                    correct
                  })
                )
              ]
            }
          ]
        : [];

      const { duration: elapsedTime } = this.getErrorLogger();

      const { activityId, grade, isLastRound } = this;

      const progressData = {
        elapsedTime,
        errorData,
        progress: {
          currentRoundId: this.currentRound.roundIndex
        },
        complete: isLastRound,
        grade,
        activityId
      };
      const { status } = await KeyboardingAPI.addScheduledActivityData(
        this.activityType,
        this.studentId,
        this.scheduledActivityId,
        progressData
      );

      if (status < 400) {
        this.advanceRound();
      } else {
        this.$store.commit('application/setModal', { type: 'error' }, { root: true });
      }
    }
  }
};
</script>

<style lang="scss" scoped>
* {
  box-sizing: border-box;
}

.activity-stage {
  // position: relative;
  padding: 10px;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  align-items: flex-start;
}

.sub-template-container {
  flex: 1;
  width: 97%;
  position: relative;

  > * {
    // We need to do this in order for the transitions.
    // When a subtemplate unmounts, we don't want it to
    // push the next one down; this way, they're overlayed
    position: absolute;
    top: 0;
    left: 0;
    width: 97%;
    height: 100%;
  }
}

h4 {
  margin: 0 10px 10px;
}

h3 {
  font-family: hwtfutura-semibold;
  margin: 10px 10px;
}

.progress-bar {
  width: 100%;
  padding: 0 30px;
}

.static-intro-button {
  position: absolute;
  right: 0;
  top: 50%;
  transform: translateY(-50%);
  z-index: 11;
}

.drag-and-drop {
  opacity: 1;
}

.fade-transition {
  transition: opacity 0.5s ease-in-out;
}

.fade-enter,
.fade-leave {
  opacity: 0;
}

.congrats {
  position: absolute;
  top: 0;
  bottom: 0;
  left: 0;
  right: 0;
  display: flex;
  justify-content: center;
  align-items: center;
}
</style>
